import { useEffect, useRef } from 'react';

/**
 * Full-screen overlay for a photo or a video.
 *
 * Closes on Escape and on a backdrop click, and returns focus to whatever
 * opened it. Scroll is locked while open, or the page drifts behind the
 * overlay on a phone. Unmounting removes the iframe, which is what actually
 * stops playback -- pausing it is not enough.
 */
export default function Lightbox({ kind, src, title, onClose }) {
  const closeRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    openerRef.current = document.activeElement;
    closeRef.current?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      // Sending focus back matters for keyboard users, who would otherwise
      // land at the top of the document each time they close one.
      openerRef.current?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-deep/90 p-4 backdrop-blur-sm sm:p-8"
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-2xl leading-none text-white transition hover:bg-white/20"
      >
        &times;
      </button>

      <figure
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-full w-full max-w-4xl flex-col items-center gap-3"
      >
        {kind === 'video' ? (
          <div className="aspect-video w-full overflow-hidden rounded-xl bg-black shadow-float">
            <iframe
              src={src}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="h-full w-full"
            />
          </div>
        ) : (
          <img
            src={src}
            alt={title}
            className="max-h-[78vh] w-auto max-w-full rounded-xl object-contain shadow-float"
          />
        )}

        {title && (
          <figcaption className="text-center font-mono text-[11px] uppercase tracking-[0.12em] text-mist">
            {title}
          </figcaption>
        )}
      </figure>
    </div>
  );
}
